import { useEffect, useMemo, useState } from "react";
import { useUserStore } from "@/store/userStore";
import { useDashboardPeriodStore } from "@/store/dashboardPeriodStore";
import type { DashboardTab } from "@/config/dashboardWidgets";
import { calculateDashboardDateRange, formatDashboardRangeLabel, type DashboardPeriod } from "@/utils/dashboardDateUtils";
import { useTranslation } from "react-i18next";
import { useSettingsStore } from '@/store/api/settingsStore';
import { dashboardBusinessDate, dashboardTimeZone } from '@/utils/dashboardBusinessTime';
import { useDashboardV2Store } from '@/store/api/dashboardV2Store';

export function useDashboardPeriod(tab: DashboardTab) {
  const { i18n } = useTranslation();
  const gymId = useUserStore((state) => state.gymId);
  const userId = useUserStore((state) => state.user?.id);
  const currency = useSettingsStore((state) => state.loadedGymId === gymId ? state.settings.language.currency : 'TND');
  const serverTimeZone = useDashboardV2Store(state => state.activeTenant === `${userId}:${gymId}` ? state.businessTimeZone : null);
  const timeZone = serverTimeZone ?? dashboardTimeZone(currency);
  const [clock, setClock] = useState(() => new Date());
  const businessDate = dashboardBusinessDate(clock, timeZone);
  useEffect(() => {
    const updateDay = () => setClock((previous) => {
      const now = new Date();
      return dashboardBusinessDate(previous, timeZone) === dashboardBusinessDate(now, timeZone) ? previous : now;
    });
    updateDay();
    // Local clock only: no HTTP polling. Wake/resume also catches a suspended tab.
    const timer = window.setInterval(updateDay, 30_000);
    window.addEventListener('focus', updateDay);
    document.addEventListener('visibilitychange', updateDay);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', updateDay);
      document.removeEventListener('visibilitychange', updateDay);
    };
  }, [timeZone]);
  const scope = `${userId ?? "anonymous"}:${gymId ?? "no-gym"}`;
  const selections = useDashboardPeriodStore((state) => state.selections);
  const setSelection = useDashboardPeriodStore((state) => state.setSelection);
  const selection = selections[`${scope}:${tab}`] ?? { period: "month" as DashboardPeriod, offset: 0 };
  const range = useMemo(
    () => calculateDashboardDateRange(selection.period, selection.offset, new Date(`${businessDate}T12:00:00Z`), timeZone),
    [selection.offset, selection.period, businessDate, timeZone],
  );
  const label = useMemo(
    () => formatDashboardRangeLabel(range, selection.period, i18n.language),
    [i18n.language, range.endDate, range.startDate, selection.period],
  );

  return {
    ...selection,
    range,
    label,
    setPeriod: (period: DashboardPeriod) => setSelection(scope, tab, { period, offset: 0 }),
    setOffset: (offset: number) => setSelection(scope, tab, { ...selection, offset: Math.max(0, offset) }),
  };
}